import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { ChangePassword } from './ChangePassword';
import QRCode from 'qrcode';

interface ProfileProps {
  darkMode: boolean;
  onUpdateSettings: () => Promise<void>;
}

export function Profile({ darkMode, onUpdateSettings }: ProfileProps) {
  const [name, setName] = useState('');
  const [logo, setLogo] = useState<string>('');
  const [photo, setPhoto] = useState<string>('');
  const [affiliateCode, setAffiliateCode] = useState('');
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [settingsId, setSettingsId] = useState<string | null>(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('user_settings')
        .select('*')
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      
      if (data) {
        setSettingsId(data.id);
        setName(data.name || '');
        setLogo(data.logo || '');
        setPhoto(data.photo || '');
        setAffiliateCode(data.affiliate_code || '');
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setMessage({ type: 'error', text: 'Failed to load profile' });
    }
  };

  const updateUserSettings = async (updateData: any) => {
    const userId = (await supabase.auth.getUser()).data.user?.id;
    if (!userId) throw new Error('No user ID found');

    if (settingsId) {
      // Update existing settings
      return await supabase
        .from('user_settings')
        .update(updateData)
        .eq('id', settingsId);
    } else {
      // Create new settings
      const result = await supabase
        .from('user_settings')
        .insert([{ ...updateData, user_id: userId }])
        .select()
        .single();
      
      if (result.data) {
        setSettingsId(result.data.id);
      }
      return result;
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'photo') => {
    try {
      const file = e.target.files?.[0];
      if (!file) return;

      setLoading(true);
      setMessage(null);

      const userId = (await supabase.auth.getUser()).data.user?.id;
      if (!userId) throw new Error('No user ID found');

      const fileExt = file.name.split('.').pop();
      const fileName = `${userId}/${type}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('uploads')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('uploads')
        .getPublicUrl(fileName);

      const updateData = type === 'logo' 
        ? { logo: publicUrl }
        : { photo: publicUrl };

      const { error: updateError } = await updateUserSettings(updateData);

      if (updateError) throw updateError;

      if (type === 'logo') setLogo(publicUrl);
      else setPhoto(publicUrl);

      setMessage({ type: 'success', text: `${type} updated successfully` });
      await onUpdateSettings();
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      setMessage({ type: 'error', text: `Failed to upload ${type}` });
    } finally {
      setLoading(false);
    }
  };

  const handleNameUpdate = async () => {
    try {
      setLoading(true);
      setMessage(null);

      const { error } = await updateUserSettings({ name });

      if (error) throw error;

      setMessage({ type: 'success', text: 'Name updated successfully' });
      await onUpdateSettings();
    } catch (error) {
      console.error('Error updating name:', error);
      setMessage({ type: 'error', text: 'Failed to update name' });
    } finally {
      setLoading(false);
    }
  };

  const handleAffiliateCodeUpdate = async () => {
    try {
      setLoading(true);
      setMessage(null);

      // Update user settings with new affiliate code
      const { error: settingsError } = await updateUserSettings({ affiliate_code: affiliateCode });
      if (settingsError) throw settingsError;

      // Update the affiliate QR code URL
      const { data: qrCode, error: qrError } = await supabase
        .from('qr_codes')
        .select('*')
        .eq('affiliate_enabled', true)
        .single();

      if (qrError && qrError.code !== 'PGRST116') throw qrError;

      if (qrCode) {
        const newUrl = affiliateCode 
          ? `https://www.danny.support/networkqr?aff=${affiliateCode}`
          : 'https://www.danny.support/networkqr';

        // Generate new QR code with specific options
        const qrData = await QRCode.toDataURL(newUrl, {
          width: 300,
          margin: 2,
          errorCorrectionLevel: 'H',
          color: {
            dark: '#000000',
            light: '#ffffff'
          }
        });

        const { error: updateError } = await supabase
          .from('qr_codes')
          .update({ 
            url: newUrl,
            qr_data: qrData
          })
          .eq('id', qrCode.id);

        if (updateError) throw updateError;
      }

      setMessage({ type: 'success', text: 'Affiliate code updated successfully' });
      await onUpdateSettings();
    } catch (error) {
      console.error('Error updating affiliate code:', error);
      setMessage({ type: 'error', text: 'Failed to update affiliate code' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      window.location.reload(); // Reload the page to reset the app state
    } catch (error) {
      console.error('Error signing out:', error);
      setMessage({ type: 'error', text: 'Failed to sign out' });
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {message && (
        <div className={`mb-4 p-3 rounded-md ${
          message.type === 'success' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
        }`}>
          {message.text}
        </div>
      )}

      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md p-6 mb-6`}>
        <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          Profile Settings
        </h2>

        <div className="space-y-6">
          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Your Name
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`flex-grow rounded-md border px-3 py-2 ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                placeholder="Enter your name"
              />
              <button
                onClick={handleNameUpdate}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Affiliate Code
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={affiliateCode}
                onChange={(e) => setAffiliateCode(e.target.value)}
                className={`flex-grow rounded-md border px-3 py-2 ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
                placeholder="Enter your affiliate code"
              />
              <button
                onClick={handleAffiliateCodeUpdate}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Save
              </button>
            </div>
            <p className={`mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Your affiliate code will be used to generate referral links for your QR codes
            </p>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Logo
            </label>
            <div className="flex items-center gap-4">
              {logo && (
                <img src={logo} alt="Logo" className="w-16 h-16 object-contain" />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload(e, 'logo')}
                disabled={loading}
                className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
              />
            </div>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Profile Photo
            </label>
            <div className="flex items-center gap-4">
              {photo && (
                <img src={photo} alt="Profile" className="w-16 h-16 rounded-full object-cover" />
              )}
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleFileUpload(e, 'photo')}
                disabled={loading}
                className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
              />
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => setShowChangePassword(true)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Change Password
            </button>

            <button
              onClick={handleLogout}
              className={`w-full px-4 py-2 rounded-md ${
                darkMode
                  ? 'bg-gray-700 text-white hover:bg-gray-600'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {showChangePassword && (
        <ChangePassword
          darkMode={darkMode}
          onClose={() => setShowChangePassword(false)}
        />
      )}
    </div>
  );
}